import {nanoid} from "nanoid/index.prod.js";
import svg_simplify from "simplify-js";

const transform_node = document.createElementNS("http://www.w3.org/2000/svg", "g");
const transform_identity = {x: 0, y: 0, r: 0, kx: 1, ky: 1, sy: 0};

export const get_translation = (transform) => {
    if (transform === null) {
        return transform_identity;
    }
    transform_node.setAttributeNS(null, "transform", transform);
    let value = transform_node.transform.baseVal.consolidate();
    if (!value) {
        return transform_identity;
    }
    let {e, f} = value.matrix;
    return {x: e, y: f, r: 0, kx: 1, ky: 1, sy: 0};
};

export const get_transform = (transform) => {
    if (transform === null) {
        return transform_identity;
    }
    transform_node.setAttributeNS(null, "transform", transform);
    let value = transform_node.transform.baseVal.consolidate().matrix;
    if (!value) {
        return transform_identity;
    }
    let matrix = value.matrix;
    // Below calculations are taken and adapted from the private function
    // transform/decompose.js of D3's module d3-interpolate.
    let {a, b, c, d, e, f} = matrix;
    let scaleX = Math.sqrt(a * a + b * b);
    let scaleY = Math.sqrt(c * c + d * d);
    let skewX = a * c + b * d;
    if (scaleX) {
        a /= scaleX;
        b /= scaleX;
    } 
    if (skewX) {
        c -= a * skewX;
        d -= b * skewX;
    }
    if (scaleY) {
        c /= scaleY;
        d /= scaleY;
        skewX /= scaleY;
    }
    if (a * d < b * c) {
        a = -a;
        b = -b;
        skewX = -skewX;
        scaleX = -scaleX;
    }
    let r = Math.atan2(b, a) * 180 / Math.PI;
    let kx = Math.atan(skewX) * 180 / Math.PI;
    return {x: e, y: f, r, kx, sx: scaleX, sy: scaleY};
};

export const simplify = svg_simplify;

const IRI_TAG_PROPERTIES_MAP = {
    clipPath: ["clip-path"],
    "color-profile": null,
    cursor: null,
    filter: null,
    linearGradient: ["fill", "stroke"],
    marker: ["marker", "marker-end", "marker-mid", "marker-start"],
    mask: ["mask"],
    pattern: ["fill", "stroke"],
    radialGradient: ["fill", "stroke"]
};
const SVG_ID_LENGTH = 10;

// This function references all the IDs within svg_elem then parse every id occurence (as direct attribute or IRI) 
// of the node and changes them if such ID is already referenced
// svg_elem = original SVG
// node = inserted SVG that must not have any ID in common with svg_elem at then end of the procedure
export const make_ids_unique = function(svg_elem, node) {
    var funcIriRegex = /url\("?#(.+)"?\)/g;
    var idElements = null;
    var node_ids_elements = null;
    var node_desc_elements = null;
    var referencedIds = [];
    var node_element;
    var tagName;
    var iriTagNames = {};
    var iriProperties = [];
    var convertedIds = {};
    
    if (!node || !svg_elem) {
        return;
    }
    node_element = node;
    
    node_ids_elements = node.querySelectorAll("[id]");
    if (!node.id && node_ids_elements.length === 0){ //no ids in the new node -> nothing to change
        return;
    }
    node_desc_elements = node.getElementsByTagName("*");
    idElements = svg_elem.querySelectorAll("[id]");
    if (idElements.length) {
        var propertyName;
        var value;
        var newValue;
        
        
        //list all tags eligible to IRI related operations
        for (let i = 0; i < idElements.length; i++) {
            tagName = idElements[i].localName; // Use non-namespaced tag name
            // Make ID unique if tag name is IRI referenceable
            if (tagName in IRI_TAG_PROPERTIES_MAP) {
                iriTagNames[tagName] = 1;
            }
        }
        // Get all properties that are mapped to the found IRI referenceable tags
        for (tagName in iriTagNames) {
            (IRI_TAG_PROPERTIES_MAP[tagName] || [tagName]).forEach(function (mappedProperty) {
                // Add mapped properties to array of iri referencing properties.
                // Use linear search here because the number of possible entries is very small (maximum 11)
                if (iriProperties.indexOf(mappedProperty) < 0) {
                    iriProperties.push(mappedProperty);
                }
            });
        }
        if (iriProperties.length) {
            // Add "style" to properties, because it may contain references in the form 'style="fill:url(#myFill)"'
            iriProperties.push("style");
        }
        
        for (let i = 0;i < idElements.length; i++) {
            let element_id = idElements[i].id;
            referencedIds[element_id] = 1;
        }
            
        for (let i = -1; node_element != null;) { //modify elements attributes/styles/hrefs
            if (node_element.id){
                let id = node_element.id;
                if (referencedIds[id]){
                    if (!convertedIds[id]){
                        convertedIds[id] = `s${nanoid(SVG_ID_LENGTH)}`;
                    }
                    node_element.id = convertedIds[id];
                }
            }
        
        
            if (node_element.localName == "style") {
                // If element is a style element, replace IDs in all occurences of "url(#anyId)" in text content
                value = node_element.textContent;
                newValue = value && value.replace(funcIriRegex, function(match, id) {
                    if (referencedIds[id]){
                        if (!convertedIds[id]){
                            convertedIds[id] = `s${nanoid(SVG_ID_LENGTH)}`;
                        }
                        return "url(#"+ convertedIds[id] + ")";
                    }
                    return "url(#"+ id + ")";
                });
                if (newValue !== value) {
                    node_element.textContent = newValue;
                }
            } else if (node_element.hasAttributes()) {
                // Run through all property names for which IDs were found
                for (let j = 0; j < iriProperties.length; j++) {
                    propertyName = iriProperties[j];
                    value = node_element.getAttribute(propertyName);
                    newValue = value && value.replace(funcIriRegex, function(match, id) {
                        if (referencedIds[id]){
                            if (!convertedIds[id]){
                                convertedIds[id] = `s${nanoid(SVG_ID_LENGTH)}`;
                            }
                            return "url(#"+ convertedIds[id] + ")";
                        }
                        return "url(#"+ id + ")";
                    });
                    if (newValue !== value) {
                        node_element.setAttribute(propertyName, newValue);
                    }
                }
                // Replace IDs in xlink:ref and href attributes
                ["xlink:href", "href"].forEach(function(refAttrName) {
                    var iri = node_element.getAttribute(refAttrName);
                    if (/^\s*#/.test(iri)) { // Check if iri is non-null and internal reference
                        iri = iri.trim();
                        
                        if (referencedIds[iri]){
                            if (!convertedIds[iri]){
                                convertedIds[iri] = `s${nanoid(SVG_ID_LENGTH)}`;
                            }
                            node_element.setAttribute(refAttrName, convertedIds[iri]);
                        }
                    }
                });
            }
            node_element = node_desc_elements[++i];
        }
    }
};

